import { ADDONS } from "@/constants/testIds";
import { Truck, Wrench, PackageOpen, Sparkles } from "lucide-react";

/* ---------- Minimal line-icon illustrations (currentColor stroke) ---------- */
const ICON_PROPS = {
  width: 40,
  height: 40,
  viewBox: "0 0 48 48",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.1,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const BalloonIcon = () => (
  <svg {...ICON_PROPS}>
    <ellipse cx="24" cy="18" rx="9" ry="12" />
    <path d="M24 30 L 24 40" />
    <path d="M22 40 L 26 40" />
  </svg>
);

const EaselIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="14" y="10" width="20" height="14" rx="1" />
    <path d="M17 42 L 22 24" />
    <path d="M31 42 L 26 24" />
    <path d="M19 34 L 29 34" />
  </svg>
);

const ClockIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="24" cy="24" r="14" />
    <path d="M24 15 L 24 24 L 31 27" />
  </svg>
);

const MapPinIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M24 7 C 17 7, 12 12, 12 19 C 12 27, 24 41, 24 41 C 24 41, 36 27, 36 19 C 36 12, 31 7, 24 7 Z" />
    <circle cx="24" cy="19" r="3" />
  </svg>
);

const BounceHouseIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M10 38 L 38 38" />
    <path d="M12 38 L 12 20 Q 24 12, 36 20 L 36 38" />
    <path d="M20 38 L 20 28 Q 24 25, 28 28 L 28 38" />
    <path d="M24 15 L 24 10" />
  </svg>
);

const StickerIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M24 8 L 27 19 L 39 19 L 29 26 L 33 38 L 24 31 L 15 38 L 19 26 L 9 19 L 21 19 Z" />
  </svg>
);

/* ---------- Add-ons data ---------- */
const ADDONS_LIST = [
  {
    key: "balloon",
    name: "Balloon Garland",
    price: "Starting at $150",
    desc: "Custom colors and designs to match your event.",
    Icon: BalloonIcon,
  },
  {
    key: "sign",
    name: "Personalized Welcome Sign",
    price: "+ $50",
    desc: "Customized with your child's name, age, and theme.",
    Icon: EaselIcon,
  },
  {
    key: "hour",
    name: "Additional Hour",
    price: "+ $50",
    desc: "Keep the fun going a little longer.",
    Icon: ClockIcon,
  },
  {
    key: "travel",
    name: "Travel Outside Service Area",
    price: "Starting at $50",
    desc: "Additional travel fee may apply.",
    Icon: MapPinIcon,
  },
  {
    key: "bounce",
    name: "Mini Bounce House 6×6",
    price: "+ $75",
    desc: "Add a little extra bounce to your celebration.",
    Icon: BounceHouseIcon,
  },
  {
    key: "decals",
    name: "Custom Decals",
    price: "Starting at $25",
    desc: "Personalized designs for bounce houses and soft play areas.",
    Icon: StickerIcon,
  },
];

/* ---------- Guarantees row ---------- */
const GUARANTEES = [
  { key: "delivery", label: "Delivery Included", Icon: Truck },
  { key: "setup", label: "Professional Setup", Icon: Wrench },
  { key: "takedown", label: "Takedown Included", Icon: PackageOpen },
  { key: "sanitized", label: "Sanitized Before Every Event", Icon: Sparkles },
];

export default function Addons() {
  return (
    <section
      id="addons"
      data-testid={ADDONS.section}
      className="relative py-24 md:py-32 bg-brand-cream/60 overflow-hidden"
    >
      {/* soft color field for the glass to refract */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 left-[20%] w-[400px] h-[400px] rounded-full bg-brand-sage/40 blur-3xl" />
        <div className="absolute bottom-0 -right-24 w-[380px] h-[380px] rounded-full bg-brand-blush/40 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-terracotta mb-5">
            Add-ons
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-brand-ink">
            Customize your event,
            <span className="italic text-brand-terracotta"> down to the detail.</span>
          </h2>
          <p className="mt-6 text-lg text-brand-mute font-light max-w-2xl">
            Mix and match small touches that turn a beautiful setup into the one
            you&apos;ll remember.
          </p>
        </div>

        {/* Add-ons grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ADDONS_LIST.map(({ key, name, price, desc, Icon }) => (
            <div
              key={key}
              data-testid={ADDONS.item(key)}
              className="group relative glass-card rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="h-14 w-14 rounded-full bg-brand-base border border-[#E9DED1] flex items-center justify-center mb-7 text-brand-terracotta transition-colors duration-300 group-hover:bg-brand-cream/70">
                <Icon />
              </div>
              <h3 className="font-serif text-2xl text-brand-ink leading-tight">{name}</h3>
              <p className="mt-2 text-brand-mute text-sm leading-relaxed">{desc}</p>
              <div className="mt-6 pt-5 border-t border-[#E9DED1]">
                <p className="text-xs uppercase tracking-[0.2em] text-brand-taupe">Pricing</p>
                <p className="font-serif text-xl text-brand-ink mt-1">{price}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Guarantees row */}
        <div
          data-testid="guarantees-row"
          className="mt-16 glass-card rounded-3xl px-6 sm:px-10 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4"
        >
          {GUARANTEES.map(({ key, label, Icon }) => (
            <div
              key={key}
              data-testid={`guarantee-${key}`}
              className="flex items-center gap-4"
            >
              <span className="shrink-0 h-12 w-12 rounded-full bg-brand-sage/50 flex items-center justify-center text-brand-ink">
                <Icon className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <p className="text-sm md:text-[15px] font-medium text-brand-ink leading-snug">
                {label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
